/**
 * PressureWashingDemo — visual preview of the Pressure Washing category
 * inside the /demo/pressure_washing route. Mock UI only; no live API calls.
 * Showcases the flagship "Aerial Map Quote" tool plus surface rates + recent
 * quotes so the user can see the shape of the product before it ships.
 */
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Droplets, MapPin, Ruler, DollarSign, Sparkles, TrendingUp,
  Calendar, ClipboardList, Home, ChevronRight, Send, Zap,
} from "lucide-react";

const SAMPLE_SURFACES = [
  { key: "driveway",     label: "Driveway",         sqft: 620, rate: 0.15, color: "#22d3ee" },
  { key: "front_walk",   label: "Front Walkway",    sqft: 180, rate: 0.20, color: "#38bdf8" },
  { key: "back_patio",   label: "Back Patio",       sqft: 340, rate: 0.18, color: "#0ea5e9" },
  { key: "house_siding", label: "House Siding",     sqft: 1250, rate: 0.30, color: "#0284c7" },
];

const RECENT_QUOTES = [
  { id: "Q-00214", customer: "Michelle Turner",   address: "412 Oak Ridge Dr",   total: 458.60, status: "sent",      when: "Today" },
  { id: "Q-00213", customer: "Anthony Rivera",     address: "88 Lakeview Ln",    total: 1284.00, status: "accepted",  when: "Yesterday" },
  { id: "Q-00212", customer: "Green Valley HOA",   address: "1200 Clubhouse Rd", total: 3420.75, status: "converted", when: "2 days ago" },
  { id: "Q-00211", customer: "Dr. Priya Sharma",   address: "27 Magnolia Ct",    total: 612.30, status: "draft",     when: "3 days ago" },
];

const STATUS_STYLES = {
  draft:     { bg: "bg-slate-200",   text: "text-slate-700" },
  sent:      { bg: "bg-sky-100",     text: "text-sky-700"    },
  accepted:  { bg: "bg-emerald-100", text: "text-emerald-700" },
  converted: { bg: "bg-violet-100",  text: "text-violet-700" },
};

/** Fake “satellite tile” background using layered radial gradients so we
 *  don’t need an actual imagery API just to demo the concept. */
const SatelliteBackground = () => (
  <div className="absolute inset-0" style={{
    background:
      "radial-gradient(ellipse at 20% 30%, #4b5f3a 0%, transparent 45%)," +
      "radial-gradient(ellipse at 75% 20%, #3f5030 0%, transparent 50%)," +
      "radial-gradient(ellipse at 65% 80%, #2f3f24 0%, transparent 55%)," +
      "radial-gradient(ellipse at 15% 85%, #4a5b3a 0%, transparent 50%)," +
      "linear-gradient(135deg, #2a3a1e 0%, #1a2412 100%)",
  }}>
    {/* Roof rectangle */}
    <div className="absolute" style={{
      left: "34%", top: "36%", width: "32%", height: "28%",
      background: "linear-gradient(180deg, #6b5a4b, #3f3428)",
      borderRadius: "3px", boxShadow: "0 4px 12px rgba(0,0,0,0.5)"
    }} />
    {/* Driveway strip */}
    <div className="absolute" style={{
      left: "40%", top: "62%", width: "8%", height: "34%",
      background: "linear-gradient(180deg, #4a4a4a, #2a2a2a)",
      borderRadius: "2px"
    }} />
    {/* Front walk */}
    <div className="absolute" style={{
      left: "48%", top: "58%", width: "12%", height: "6%",
      background: "#5a5a5a", borderRadius: "2px"
    }} />
    {/* Patio */}
    <div className="absolute" style={{
      left: "60%", top: "38%", width: "15%", height: "16%",
      background: "linear-gradient(135deg, #8a7a68, #5a4d3f)",
      borderRadius: "3px"
    }} />
    {/* Grid overlay */}
    <div className="absolute inset-0 opacity-10" style={{
      backgroundImage:
        "linear-gradient(rgba(255,255,255,0.15) 1px, transparent 1px)," +
        "linear-gradient(90deg, rgba(255,255,255,0.15) 1px, transparent 1px)",
      backgroundSize: "40px 40px"
    }} />
  </div>
);

/** Polygon overlays on the mock map — each surface gets a colored shape */
const PolygonOverlays = () => (
  <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 400 300" preserveAspectRatio="none">
    {/* Driveway */}
    <polygon
      points="160,190 190,190 195,290 158,290"
      fill="rgba(34,211,238,0.35)"
      stroke="#22d3ee"
      strokeWidth="2"
      strokeDasharray="4 3"
    />
    {/* Front walk */}
    <polygon
      points="195,178 240,178 240,192 195,192"
      fill="rgba(56,189,248,0.35)"
      stroke="#38bdf8"
      strokeWidth="2"
      strokeDasharray="4 3"
    />
    {/* Back patio */}
    <polygon
      points="243,115 302,115 302,160 243,160"
      fill="rgba(14,165,233,0.35)"
      stroke="#0ea5e9"
      strokeWidth="2"
      strokeDasharray="4 3"
    />
    {/* Corner handles for driveway (looks like drawing tool active) */}
    {[
      [160, 190], [190, 190], [195, 290], [158, 290],
    ].map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r="4" fill="#fff" stroke="#0891b2" strokeWidth="2" />
    ))}
  </svg>
);

export default function PressureWashingDemo({ isDark }) {
  const [selectedSurface, setSelectedSurface] = useState("driveway");

  const totalSqft = SAMPLE_SURFACES.reduce((s, x) => s + x.sqft, 0);
  const subtotal = SAMPLE_SURFACES.reduce((s, x) => s + x.sqft * x.rate, 0);
  const total = Math.max(subtotal, 150);

  const stats = [
    { label: "This Month",  value: "$28,540", icon: DollarSign,  tint: "text-emerald-500" },
    { label: "Quotes Sent", value: "37",      icon: Send,         tint: "text-sky-500" },
    { label: "Jobs Booked", value: "24",      icon: Calendar,     tint: "text-cyan-500" },
    { label: "Conv. Rate",  value: "65%",     icon: TrendingUp,   tint: "text-violet-500" },
  ];

  const card = isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200";
  const subtext = isDark ? "text-slate-400" : "text-slate-500";
  const heading = isDark ? "text-white" : "text-slate-800";

  return (
    <div className="space-y-6" data-testid="pressure-washing-demo">
      {/* Preview banner */}
      <div className={`p-4 rounded-lg border ${isDark ? "bg-cyan-900/20 border-cyan-700 text-cyan-200" : "bg-cyan-50 border-cyan-200 text-cyan-800"}`}>
        <div className="flex items-center gap-3">
          <Sparkles className="w-5 h-5" />
          <div>
            <p className="font-semibold">Preview — Pressure Washing category</p>
            <p className={`text-sm ${isDark ? "text-cyan-300" : "text-cyan-700"}`}>
              Draw driveways, patios and roofs on aerial imagery. Sqft is measured for you and priced from your per-surface rates.
            </p>
          </div>
        </div>
      </div>

      {/* Stat row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {stats.map((s) => (
          <Card key={s.label} className={card}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className={`text-xs font-medium ${subtext}`}>{s.label}</p>
                <s.icon className={`w-4 h-4 ${s.tint}`} />
              </div>
              <p className={`text-xl font-bold mt-1 ${heading}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Flagship: Aerial Map Quote */}
      <Card className={card}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className={`flex items-center gap-2 ${heading}`}>
              <MapPin className="w-5 h-5 text-cyan-500" />
              Instant Aerial Quote
              <Badge className="bg-cyan-100 text-cyan-700 border border-cyan-300 hover:bg-cyan-100">MVP</Badge>
            </CardTitle>
            <div className="flex items-center gap-2">
              <div className={`text-sm ${subtext}`}>
                <span className="font-mono">412 Oak Ridge Dr, Madison WI</span>
              </div>
              <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="demo-search-address-btn">
                Search
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {/* Map */}
            <div className="lg:col-span-3">
              <div className="relative rounded-lg overflow-hidden border border-slate-700" style={{ aspectRatio: "4 / 3" }}>
                <SatelliteBackground />
                <PolygonOverlays />
                {/* Floating toolbar */}
                <div className="absolute top-3 left-3 flex flex-col gap-1.5 bg-slate-900/90 backdrop-blur rounded-lg p-1.5 border border-slate-700">
                  {SAMPLE_SURFACES.slice(0, 4).map((s) => (
                    <button
                      key={s.key}
                      onClick={() => setSelectedSurface(s.key)}
                      className={`w-8 h-8 rounded-md flex items-center justify-center transition-all ${
                        selectedSurface === s.key ? "ring-2 ring-white" : "opacity-60 hover:opacity-100"
                      }`}
                      style={{ background: s.color }}
                      title={s.label}
                    >
                      <Ruler className="w-4 h-4 text-slate-900" />
                    </button>
                  ))}
                </div>
                {/* Sqft callout */}
                <div className="absolute bottom-3 left-3 bg-slate-900/90 backdrop-blur rounded-lg px-3 py-2 border border-slate-700">
                  <p className="text-[10px] uppercase tracking-wide text-slate-400">Selected polygon</p>
                  <p className="text-sm font-semibold text-white flex items-center gap-2">
                    <Ruler className="w-3.5 h-3.5 text-cyan-400" />
                    Driveway · <span className="text-cyan-300">620 sqft</span>
                  </p>
                </div>
                {/* Instructions overlay */}
                <div className="absolute top-3 right-3 bg-slate-900/90 backdrop-blur rounded-lg px-3 py-2 border border-slate-700 max-w-[180px]">
                  <p className="text-[10px] text-cyan-300 font-semibold uppercase mb-0.5">How it works</p>
                  <p className="text-[11px] text-slate-300 leading-snug">
                    Tap each surface on the map. We measure the sqft and price it from your rates.
                  </p>
                </div>
              </div>
            </div>

            {/* Quote breakdown */}
            <div className="lg:col-span-2 space-y-3">
              <div className="space-y-2">
                {SAMPLE_SURFACES.map((s) => {
                  const line = s.sqft * s.rate;
                  const active = selectedSurface === s.key;
                  return (
                    <div
                      key={s.key}
                      className={`p-3 rounded-lg border transition-all ${
                        active
                          ? isDark ? "border-cyan-500 bg-cyan-950/40" : "border-cyan-500 bg-cyan-50"
                          : isDark ? "border-slate-700 bg-slate-700/40" : "border-slate-200 bg-slate-50"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} />
                          <p className={`text-sm font-medium ${heading}`}>{s.label}</p>
                        </div>
                        <p className={`text-sm font-semibold ${heading}`}>${line.toFixed(2)}</p>
                      </div>
                      <p className={`text-xs mt-0.5 ${subtext}`}>
                        {s.sqft.toLocaleString()} sqft × ${s.rate.toFixed(2)}/sqft
                      </p>
                    </div>
                  );
                })}
              </div>

              <div className={`p-3 rounded-lg border ${isDark ? "border-slate-700 bg-slate-900/40" : "border-slate-200 bg-slate-100/70"}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className={subtext}>Total sqft measured</span>
                  <span className={`font-mono ${heading}`}>{totalSqft.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between text-sm mt-1">
                  <span className={subtext}>Subtotal</span>
                  <span className={`font-mono ${heading}`}>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-dashed border-slate-500/40">
                  <span className={`font-semibold ${heading}`}>Quote total</span>
                  <span className="text-2xl font-bold text-cyan-500">${total.toFixed(2)}</span>
                </div>
              </div>

              <div className="flex gap-2">
                <Button className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="demo-send-quote-btn">
                  <Send className="w-4 h-4 mr-1.5" /> Send Quote
                </Button>
                <Button variant="outline" className="flex-1" data-testid="demo-book-job-btn">
                  <Zap className="w-4 h-4 mr-1.5" /> Book Job
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rates + recent quotes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className={card}>
          <CardHeader className="pb-3">
            <CardTitle className={`flex items-center gap-2 ${heading}`}>
              <DollarSign className="w-5 h-5 text-emerald-500" />
              Your Per-Surface Rates
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-500/20">
              {[
                ...SAMPLE_SURFACES,
                { key: "roof",     label: "Roof (soft wash)", sqft: 0, rate: 0.40, color: "#0369a1" },
                { key: "fence",    label: "Fence",            sqft: 0, rate: 0.25, color: "#075985" },
              ].map((s) => (
                <div key={s.key} className="flex items-center justify-between px-4 py-2.5">
                  <div className="flex items-center gap-2.5">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} />
                    <p className={`text-sm ${heading}`}>{s.label}</p>
                  </div>
                  <p className={`text-sm font-mono ${subtext}`}>${s.rate.toFixed(2)}/sqft</p>
                </div>
              ))}
            </div>
            <div className={`px-4 py-3 border-t border-slate-500/20 flex items-center justify-between ${isDark ? "bg-slate-900/40" : "bg-slate-50"}`}>
              <span className={`text-xs ${subtext}`}>Minimum charge per job</span>
              <span className={`text-sm font-semibold ${heading}`}>$150.00</span>
            </div>
          </CardContent>
        </Card>

        <Card className={card}>
          <CardHeader className="pb-3">
            <CardTitle className={`flex items-center gap-2 ${heading}`}>
              <ClipboardList className="w-5 h-5 text-cyan-500" />
              Recent Quotes
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-500/20">
              {RECENT_QUOTES.map((q) => {
                const style = STATUS_STYLES[q.status];
                return (
                  <div key={q.id} className="flex items-center gap-3 px-4 py-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isDark ? "bg-slate-700" : "bg-slate-100"}`}>
                      <Home className={`w-4 h-4 ${subtext}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${heading}`}>{q.customer}</p>
                      <p className={`text-xs truncate ${subtext}`}>{q.address} · {q.when}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-semibold ${heading}`}>${q.total.toFixed(2)}</p>
                      <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${style.bg} ${style.text} uppercase tracking-wide`}>
                        {q.status}
                      </span>
                    </div>
                    <ChevronRight className={`w-4 h-4 ${subtext}`} />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Feature strip */}
      <Card className={card}>
        <CardHeader className="pb-3">
          <CardTitle className={heading}>What ships with Pressure Washing</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { icon: MapPin,       title: "Aerial polygon quoting",  desc: "Free satellite imagery. Draw driveways, patios, roofs — sqft measured for you." },
              { icon: DollarSign,   title: "Per-surface rates",       desc: "Editable rates per surface + minimum job charge. Every quote priced consistently." },
              { icon: Send,         title: "Quote → Invoice → Paid",  desc: "Convert an accepted quote to a Stripe/Zelle/Cash invoice in one click." },
            ].map((f) => (
              <div key={f.title} className={`p-4 rounded-lg ${isDark ? "bg-slate-700/40" : "bg-slate-50"}`}>
                <f.icon className="w-5 h-5 text-cyan-500 mb-2" />
                <p className={`font-semibold text-sm ${heading}`}>{f.title}</p>
                <p className={`text-xs mt-1 ${subtext}`}>{f.desc}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
